import React, { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import api from "../../services/api"
import WorkoutModal from "../WorkoutModal"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Skeleton } from "../ui/skeleton"
import { StatusBadge, BooleanBadge } from "../StatusBadge"
import { DeleteConfirmDialog } from "../ConfirmDialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { List, Eye, Trash2, X } from "lucide-react"

function WorkoutTable({ adminOnly }) {
  const queryClient = useQueryClient()
  
  // Modal state
  const [selectedWorkout, setSelectedWorkout] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteWorkout, setDeleteWorkout] = useState(null)

  // Pagination state
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  // Sorting state
  const [sortBy, setSortBy] = useState("workoutStartTime")
  const [sortOrder, setSortOrder] = useState("desc")

  // Filtering state
  const [filters, setFilters] = useState({
    status: "",
    source: "",
    dateFrom: "",
    dateTo: "",
  })

  // Debounced filter values
  const [debouncedFilters, setDebouncedFilters] = useState(filters)

  // Debounce filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters)
      setCurrentPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [filters])

  // Query for workouts
  const { data, isLoading } = useQuery({
    queryKey: [
      "workouts",
      adminOnly,
      pageSize,
      currentPage,
      sortBy,
      sortOrder,
      debouncedFilters,
    ],
    queryFn: () => {
      const params = {
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
        sortBy,
        sortOrder,
        adminOnly,
      }
      if (debouncedFilters.status) params.status = debouncedFilters.status
      if (debouncedFilters.source) params.source = debouncedFilters.source
      if (debouncedFilters.dateFrom) params.dateFrom = debouncedFilters.dateFrom
      if (debouncedFilters.dateTo) params.dateTo = debouncedFilters.dateTo
      return api.getWorkouts(params)
    },
  })

  const workouts = data?.workouts || []
  const totalCount = data?.total || 0

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (sessionID) => api.deleteWorkout(sessionID),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workouts"] })
      queryClient.invalidateQueries({ queryKey: ["stats"] })
      setDeleteWorkout(null)
      toast.success("Workout deleted successfully")
    },
    onError: (error) => {
      toast.error(`Failed to delete workout: ${error.message}`)
    },
  })

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortBy(field)
      setSortOrder("desc")
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value })
  }

  const handleViewWorkout = (workout) => {
    setSelectedWorkout(workout)
    setIsModalOpen(true)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDuration = (seconds) => {
    if (!seconds) return "-"
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const totalPages = Math.ceil(totalCount / pageSize)

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return <span className="text-muted-foreground ml-1">↕</span>
    }
    return <span className="ml-1">{sortOrder === "asc" ? "↑" : "↓"}</span>
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Workout History
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value))
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                handleFilterChange("status", value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.source || "all"}
              onValueChange={(value) =>
                handleFilterChange("source", value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="aiGenerated">AI Generated</SelectItem>
                <SelectItem value="userTemplate">User Template</SelectItem>
                <SelectItem value="quickStart">Quick Start</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className="w-36"
            />
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className="w-36"
            />
            <Button
              variant="secondary"
              onClick={() =>
                setFilters({
                  status: "",
                  source: "",
                  dateFrom: "",
                  dateTo: "",
                })
              }
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        onClick={() => handleSort("workoutStartTime")}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        Date <SortIcon field="workoutStartTime" />
                      </TableHead>
                      <TableHead
                        onClick={() => handleSort("anonymousUserID")}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        User ID <SortIcon field="anonymousUserID" />
                      </TableHead>
                      <TableHead
                        onClick={() => handleSort("completionStatus")}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        Status <SortIcon field="completionStatus" />
                      </TableHead>
                      <TableHead
                        onClick={() => handleSort("workoutSource")}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        Source <SortIcon field="workoutSource" />
                      </TableHead>
                      <TableHead
                        onClick={() => handleSort("workoutRating")}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        Rating <SortIcon field="workoutRating" />
                      </TableHead>
                      <TableHead
                        onClick={() => handleSort("workoutDurationSeconds")}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        Duration <SortIcon field="workoutDurationSeconds" />
                      </TableHead>
                      <TableHead className="whitespace-nowrap">Voice</TableHead>
                      <TableHead className="whitespace-nowrap">Watch</TableHead>
                      <TableHead className="whitespace-nowrap">Ex</TableHead>
                      <TableHead className="whitespace-nowrap">Sets</TableHead>
                      <TableHead className="whitespace-nowrap">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workouts.map((workout) => {
                      const exerciseCount =
                        workout.blocks?.reduce(
                          (acc, block) => acc + (block.exercises?.length || 0),
                          0
                        ) || 0
                      const setCount =
                        workout.blocks?.reduce(
                          (acc, block) =>
                            acc +
                            (block.exercises?.reduce(
                              (setAcc, exercise) =>
                                setAcc + (exercise.sets?.length || 0),
                              0
                            ) || 0),
                          0
                        ) || 0

                      return (
                        <TableRow key={workout._id || workout.sessionID}>
                          <TableCell className="whitespace-nowrap">
                            {formatDate(
                              workout.workoutStartTime || workout.serverReceivedAt
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {workout.anonymousUserID?.substring(0, 8)}...
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={workout.completionStatus}>
                              {workout.completionStatus}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{workout.workoutSource || "-"}</TableCell>
                          <TableCell className="text-yellow-500">
                            {workout.workoutRating
                              ? "★".repeat(workout.workoutRating)
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {formatDuration(workout.workoutDurationSeconds)}
                          </TableCell>
                          <TableCell>
                            <BooleanBadge
                              value={workout.voiceCoachEnabled}
                              trueLabel="On"
                              falseLabel="Off"
                            />
                          </TableCell>
                          <TableCell>
                            <BooleanBadge value={workout.appleWatchUsed} />
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {exerciseCount}
                          </TableCell>
                          <TableCell className="tabular-nums">{setCount}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewWorkout(workout)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setDeleteWorkout(workout)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {workouts.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No workouts found
                </div>
              )}

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                  workouts
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <WorkoutModal
        workout={selectedWorkout}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedWorkout(null)
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        title="Delete this workout?"
        description={
          deleteWorkout
            ? `Session ID: ${deleteWorkout.sessionID}\nUser: ${deleteWorkout.anonymousUserID?.substring(0, 8)}...\nDate: ${formatDate(deleteWorkout.workoutStartTime)}`
            : ""
        }
        onConfirm={() => {
          if (deleteWorkout) {
            deleteMutation.mutate(deleteWorkout.sessionID)
          }
        }}
        open={!!deleteWorkout}
        onOpenChange={(open) => !open && setDeleteWorkout(null)}
      />
    </>
  )
}

export default WorkoutTable
